import type { WebMCPTool } from "../core/tool-registry"

const DESTRUCTIVE_NAME_PATTERN =
  /(?:^|[._-])(delete|remove|checkout|pay|set_state|clear_cart)(?:$|[._-])/
const CONFIRMATION_REQUIRED_TAGS = new Set([
  "confirmation.required",
  "crud.delete",
  "checkout",
  "payment",
])
const HIGH_RISK_RE = /delete|pay|checkout|clear/

export interface ConfirmationResult {
  needsConfirmation: true
  toolName: string
  toolDescription: string
  args: Record<string, unknown>
  callId: string
  /** true for delete/payment, false for set_state */
  isDestructive: boolean
}

/**
 * Programmatic confirmation check. Returns true when the tool
 * should require explicit user approval before execution.
 */
export function requiresConfirmation(
  tool: WebMCPTool,
  _args?: Record<string, unknown>,
): boolean {
  if (tool.metadata?.confirmation === "always") return true
  // Destructive names and high-risk tags are authoritative. Incorrect tool
  // metadata must never turn a destructive operation into a silent action.
  if (DESTRUCTIVE_NAME_PATTERN.test(normalizeToolName(tool.name))) return true
  if (tool.metadata?.tags?.some((t) => CONFIRMATION_REQUIRED_TAGS.has(t))) {
    return true
  }
  // Read-only tools do not require confirmation unless explicitly marked always.
  if (tool.metadata?.readOnly) return false
  if (tool.metadata?.confirmation === "when-mutating") return true
  // Explicit never can suppress confirmation only after destructive names and
  // high-risk tags have been checked.
  if (tool.metadata?.confirmation === "never") return false
  return false
}

function normalizeToolName(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
}

/**
 * Build a confirmation result payload for a pending tool call.
 */
export function buildConfirmation(
  tool: WebMCPTool,
  args: Record<string, unknown>,
  callId: string,
): ConfirmationResult {
  const isDestructive =
    HIGH_RISK_RE.test(normalizeToolName(tool.name)) ||
    !!tool.metadata?.tags?.some((t) => t === "crud.delete" || t === "payment")

  return {
    needsConfirmation: true,
    toolName: tool.name,
    toolDescription: tool.description,
    args,
    callId,
    isDestructive,
  }
}
